import { z } from 'zod';

/** The kinds of resource an eligibility check can judge and an approver can override a block on. */
export const ELIGIBILITY_OVERRIDE_RESOURCES = ['truck', 'driver', 'trailer'] as const;
export type EligibilityOverrideResource = (typeof ELIGIBILITY_OVERRIDE_RESOURCES)[number];

/** `kind` on the `overrides` row, so the M11 override report (`GET /overrides?kind=`) lists these. */
export const ELIGIBILITY_OVERRIDE_KIND = 'dispatch_eligibility';

const isoInstant = z.iso.datetime({ offset: true });

/**
 * `POST /trips/:id/eligibility-override` (gitaumoses4/chukua#51): the approver, in one call, overrides one blocking check on one resource
 * for this trip and names the dispatcher who asked. The window and pickup are what the check is judged against, since a trip carries none
 * until assignment; the zones come from the trip.
 */
export const grantEligibilityOverrideSchema = z
  .object({
    resource: z.enum(ELIGIBILITY_OVERRIDE_RESOURCES),
    resourceId: z.uuid(),
    checkCode: z.string().trim().min(1).max(64),
    reason: z.string().trim().min(1).max(500),
    requestedById: z.uuid(),
    windowStart: isoInstant.transform((value) => new Date(value)),
    windowEnd: isoInstant.transform((value) => new Date(value)),
    pickup: z.object({ zoneCode: z.string().trim().min(1).max(32), at: isoInstant.transform((value) => new Date(value)) }).optional(),
  })
  .refine((input) => input.windowEnd > input.windowStart, { message: 'The window must end after it starts', path: ['windowEnd'] });
export type GrantEligibilityOverrideInput = z.output<typeof grantEligibilityOverrideSchema>;

export interface EligibilityOverrideUser {
  id: string;
  name: string;
}

/** One recorded override as the web shows it next to the check it covers. */
export interface EligibilityOverrideView {
  id: string;
  resource: EligibilityOverrideResource;
  resourceId: string;
  checkCode: string;
  reason: string;
  approver: EligibilityOverrideUser | null;
  requester: EligibilityOverrideUser | null;
  grantedAt: string;
}
